// GtTable — schema-driven candidates grid with frozen columns + horizontal scroll.
// Mechanics ported from CE CrmTable (read end to end 21 Jul 2026); renderers are
// GTCO recruiting semantics: lifecycle status, right to work, rate, CV state, tags.
import { useState, useEffect } from 'react';
import GtLoader from './GtLoader';
import { SearchMatchLine } from './gtSearch'; // C1-SEARCH
import { TempDot } from './gtTemp'; // C1-EMAIL

function initials(name) {
  return (name || '?').split(' ').map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
}

// Stable per-name avatar colour: same name -> same colour.
const AVATAR_COLOURS = [
  '#1E5A7B', '#2E7D32', '#6A1B9A', '#B8860B', '#00695C', '#C2410C',
  '#374151', '#9D174D', '#1565C0', '#4E342E', '#5B6C1E', '#0F766E',
  '#B91C1C', '#5D4037', '#3949AB', '#00838F',
];
function avatarColour(name) {
  const s = String(name || '?');
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return AVATAR_COLOURS[h % AVATAR_COLOURS.length];
}

// Lifecycle pills: the GTCO candidate pipeline, not sales stages.
const STATUS_PILL = {
  new:                  { c: 'gtt-p-slate', l: 'New' },
  parsing:              { c: 'gtt-p-slate', l: 'Parsing' },
  enriched:             { c: 'gtt-p-blue',  l: 'Enriched' },
  invited_to_interview: { c: 'gtt-p-amber', l: 'Invited' },
  interviewing:         { c: 'gtt-p-amber', l: 'Interviewing' },
  assessed:             { c: 'gtt-p-blue',  l: 'Assessed' },
  available:            { c: 'gtt-p-green', l: 'Available' },
  submitted:            { c: 'gtt-p-teal',  l: 'Submitted' },
  placed:               { c: 'gtt-p-green', l: 'Placed' },
  archived:             { c: 'gtt-p-slate', l: 'Archived' },
};

// Right to work: a placement decision fact — colour carries the meaning.
const RTW_PILL = {
  citizen:            { c: 'gtt-p-green', l: 'Citizen' },
  permanent_resident: { c: 'gtt-p-green', l: 'Perm. resident' },
  visa_holder:        { c: 'gtt-p-blue',  l: 'Visa holder' },
  needs_sponsorship:  { c: 'gtt-p-amber', l: 'Needs sponsorship' },
  unknown:            { c: 'gtt-p-slate', l: 'Unknown' },
};

const LANE_LABEL = { self_serve: 'Self serve', bulk_import: 'Bulk import', manual: 'Manual', bulk_cv: 'Bulk CV' };
const CONSENT_PILL = {
  explicit:  { c: 'gtt-p-green', l: 'Explicit' },
  none:      { c: 'gtt-p-slate', l: 'None' },
  withdrawn: { c: 'gtt-p-rose',  l: 'Withdrawn' },
};

// Role pipeline pills (roles table reuses this schema-driven grid).
const ROLE_STATUS_PILL = {
  draft:  { c: 'gtt-p-slate', l: 'Draft' },
  open:   { c: 'gtt-p-green', l: 'Open' },
  paused: { c: 'gtt-p-amber', l: 'Paused' },
  filled: { c: 'gtt-p-blue',  l: 'Filled' },
  closed: { c: 'gtt-p-slate', l: 'Closed' },
};
const PLACEMENT_PILL = {
  permanent_fee:      { c: 'gtt-p-teal', l: 'Permanent' },
  contract_rate_card: { c: 'gtt-p-blue', l: 'Contract' },
};
const CLIENT_STATUS_PILL = {
  prospect: { c: 'gtt-p-slate', l: 'Prospect' },
  active:   { c: 'gtt-p-green', l: 'Active' },
  dormant:  { c: 'gtt-p-amber', l: 'Dormant' },
  archived: { c: 'gtt-p-slate', l: 'Archived' },
};

function ukDate(v) {
  if (!v) return null;
  const d = new Date(v); if (isNaN(d)) return null;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()}`;
}

function formatRate(gbp, unit) {
  if (gbp == null || gbp === '') return null;
  const n = Number(gbp);
  if (!isFinite(n)) return null;
  const amount = n >= 1000 ? '£' + Math.round(n).toLocaleString('en-GB') : '£' + n.toLocaleString('en-GB');
  const suffix = unit === 'year' ? '/yr' : unit === 'day' ? '/day' : unit === 'hour' ? '/hr' : '';
  return amount + suffix;
}

function Cell({ col, row, allTags, onTagToggle, onCandidateClick }) {
  const [tagPop, setTagPop] = useState(null); // {x, y} when open
  useEffect(() => {
    if (!tagPop) return;
    const close = () => setTagPop(null);
    document.addEventListener('click', close);
    window.addEventListener('scroll', close, true);
    return () => { document.removeEventListener('click', close); window.removeEventListener('scroll', close, true); };
  }, [tagPop]);
  const v = row[col.key];
  if (typeof col.cell === 'function') return col.cell(row); // a screen's own cell (Auto outreach)

  switch (col.render) {
    case 'id':
      return <span className="gtt-id">#{row.id}</span>;
    case 'candidate': {
      const nm = row.full_name || [row.first_name, row.last_name].filter(Boolean).join(' ') || 'Unnamed';
      return (
        <div className="gtt-cname">
          <span className="gtt-av" style={{ background: avatarColour(nm) }}>{initials(nm)}</span>
          <div className="gtt-namebox">
            {onCandidateClick
              ? <button type="button" className="gtt-nm gtt-nm-link" onClick={() => onCandidateClick(row)}>{nm}</button>
              : <div className="gtt-nm">{nm}</div>}
            {row.headline ? <div className="gtt-sub">{row.headline}</div> : null}
            {row.search_match ? <SearchMatchLine m={row.search_match} /> : null}
          </div>
        </div>
      );
    }
    case 'tags': {
      const tags = Array.isArray(v) ? v : [];
      const assignedIds = new Set(tags.map(t => t.id));
      const editable = Array.isArray(allTags) && typeof onTagToggle === 'function';
      return (
        <div className="gtt-tagcell" onClick={e => { if (editable) { e.stopPropagation(); const rect = e.currentTarget.getBoundingClientRect(); setTagPop(p => p ? null : { x: rect.left, y: rect.bottom + 4 }); } }}>
          <span className="gtt-chips">
            {tags.length === 0 && !editable && <span className="gtt-dash">—</span>}
            {tags.length === 0 && editable && <span className="gtt-tagempty">+ Add tag</span>}
            {tags.slice(0, 3).map(t => (
              <span key={t.id} className="gtt-chip" style={{ color: t.colour, background: (t.colour || '#1FA9B2') + '22' }}>{t.name}</span>
            ))}
            {tags.length > 3 ? <span className="gtt-chip gtt-chip-more">+{tags.length - 3}</span> : null}
            {editable && tags.length > 0 && <span className="gtt-tagadd" title="Edit tags">+</span>}
          </span>
          {editable && tagPop && (
            <div className="gtt-tagpop" style={{ left: tagPop.x, top: tagPop.y }} onClick={e => e.stopPropagation()}>
              {allTags.length === 0 && <div className="gtt-tagpop-empty">No tags yet. Create one via the Tags button.</div>}
              {allTags.map(t => {
                const on = assignedIds.has(t.id);
                return (
                  <div key={t.id} className="gtt-tagpop-item" onClick={() => onTagToggle(row.id, t.id, on)}>
                    <span className="gtt-tagpop-dot" style={{ background: t.colour }} />
                    <span>{t.name}</span>
                    {on && <span className="gtt-tagpop-check">✓</span>}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      );
    }
    case 'domain':
      return v ? <span className="gtt-pill gtt-p-domain">{String(v).replace(/_/g, ' ')}</span> : <span className="gtt-dash">—</span>;
    case 'status': {
      const p = STATUS_PILL[String(v || 'new').toLowerCase()] || STATUS_PILL.new;
      return <span className={`gtt-pill ${p.c}`}>{p.l}</span>;
    }
    case 'rtw': {
      if (!v) return <span className="gtt-dash">—</span>;
      const p = RTW_PILL[String(v).toLowerCase()] || { c: 'gtt-p-slate', l: v };
      return <span className={`gtt-pill ${p.c}`}>{p.l}</span>;
    }
    case 'rate': {
      const t = formatRate(row.expected_rate_gbp, row.expected_rate_unit);
      return t ? <span className="gtt-rate">{t}</span> : <span className="gtt-dash">—</span>;
    }
    case 'cv': {
      if (!row.cv_file_path) return <span className="gtt-pill gtt-p-slate">None</span>;
      if (row.has_parse_flags) return <span className="gtt-pill gtt-p-amber" title="Low-confidence fields need review">Flagged</span>;
      return <span className="gtt-pill gtt-p-green">Parsed</span>;
    }
    case 'lane':
      return v ? <span className="gtt-ctext">{LANE_LABEL[v] || v}</span> : <span className="gtt-dash">—</span>;
    case 'consent': {
      if (!v) return <span className="gtt-dash">—</span>;
      const p = CONSENT_PILL[String(v).toLowerCase()] || { c: 'gtt-p-slate', l: v };
      return <span className={`gtt-pill ${p.c}`}>{p.l}</span>;
    }
    case 'countpill': {
      const n = v != null && v !== '' ? Number(v) : 0;
      return n ? <span className="gtt-pill gtt-p-teal">{n}</span> : <span className="gtt-dash">—</span>;
    }
    case 'email':
      return v ? <a className="gtt-link" href={`mailto:${v}`} onClick={e => e.stopPropagation()}>{v}</a> : <span className="gtt-dash">—</span>;
    case 'link':
      return v ? <a className="gtt-link" href={v.startsWith('http') ? v : 'https://' + v} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}>View</a> : <span className="gtt-dash">—</span>;
    case 'github':
      return v ? <a className="gtt-link" href={'https://github.com/' + v} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}>{v}</a> : <span className="gtt-dash">—</span>;
    case 'ukdate': {
      const d = ukDate(v);
      return d ? <span className="gtt-date">{d}</span> : <span className="gtt-dash">—</span>;
    }
    case 'role': {
      const rnm = row.title || 'Untitled role';
      return (
        <div className="gtt-cname">
          <span className="gtt-av" style={{ background: avatarColour(rnm) }}>{initials(rnm)}</span>
          <div className="gtt-namebox">
            {onCandidateClick
              ? <button type="button" className="gtt-nm gtt-nm-link" onClick={() => onCandidateClick(row)}>{rnm}</button>
              : <div className="gtt-nm">{rnm}</div>}
            {row.client_name ? <div className="gtt-sub">{row.client_name}</div> : null}
            {row.search_match ? <SearchMatchLine m={row.search_match} /> : null}
          </div>
        </div>
      );
    }
    case 'rolestatus': {
      const rp = ROLE_STATUS_PILL[String(v || 'open').toLowerCase()] || ROLE_STATUS_PILL.open;
      return <span className={`gtt-pill ${rp.c}`}>{rp.l}</span>;
    }
    case 'placement': {
      if (!v) return <span className="gtt-dash">—</span>;
      const pp = PLACEMENT_PILL[String(v).toLowerCase()] || { c: 'gtt-p-slate', l: v };
      return <span className={`gtt-pill ${pp.c}`}>{pp.l}</span>;
    }
    case 'feepct':
      return (v != null && v !== '') ? <span className="gtt-ctext">{Number(v)}%</span> : <span className="gtt-dash">—</span>;
    case 'ref':
      return v ? <span className="gtt-ref">{v}</span> : <span className="gtt-id">#{row.id}</span>;
    case 'client': {
      const cnm = row.name || 'Unnamed client';
      return (
        <div className="gtt-cname">
          {row.logo_data
            ? <span className="gtt-av gtt-av--logo"><img src={row.logo_data} alt="" /></span>
            : <span className="gtt-av" style={{ background: avatarColour(cnm) }}>{initials(cnm)}</span>}
          <div className="gtt-namebox">
            {onCandidateClick
              ? <button type="button" className="gtt-nm gtt-nm-link" onClick={() => onCandidateClick(row)}>{cnm}</button>
              : <div className="gtt-nm">{cnm}</div>}
            {row.sector ? <div className="gtt-sub">{row.sector}</div> : null}
            {row.search_match ? <SearchMatchLine m={row.search_match} /> : null}
          </div>
        </div>
      );
    }
    case 'clientstatus': {
      const cp = CLIENT_STATUS_PILL[String(v || 'prospect').toLowerCase()] || CLIENT_STATUS_PILL.prospect;
      return <span className={`gtt-pill ${cp.c}`}>{cp.l}</span>;
    }
    case 'interview': {
      if (!row.interview_id) return <span className="gtt-dash">—</span>;
      const forRole = row.interview_role ? ' for ' + row.interview_role : '';
      if (row.interview_scoring === 'done') {
        const BAND = { strong: { c: 'gtt-p-green', l: 'Strong' }, possible: { c: 'gtt-p-amber', l: 'Possible' }, weak: { c: 'gtt-p-slate', l: 'Weak' }, incomplete: { c: 'gtt-p-slate', l: 'Incomplete' } };
        const pct = row.interview_score != null && row.interview_score !== '' ? Math.round(Number(row.interview_score)) : null;
        const bp = BAND[row.interview_band] || { c: 'gtt-p-slate', l: '' };
        const flagged = !!row.interview_integrity && row.interview_integrity !== 'clear';
        const tip = (pct == null ? (row.interview_band === 'incomplete' ? 'Too little of the rubric covered to score' : 'Not enough said to score') : pct + '%, ' + bp.l.toLowerCase() + ' match') + forRole
          + (flagged ? '. Integrity: ' + row.interview_integrity : '')
          + (row.interview_decision ? '. Reviewed: ' + String(row.interview_decision).replace(/_/g, ' ') : '. Not yet reviewed');
        return (
          <span className="gtt-chips" title={tip}>
            <span className={`gtt-pill ${bp.c}`}>{pct == null ? (row.interview_band === 'incomplete' ? 'Incomplete' : 'No score') : pct + '% ' + bp.l}</span>
            {flagged ? <span className="gtt-pill gtt-p-amber">Check</span> : null}
          </span>
        );
      }
      const STATE = { invited: { c: 'gtt-p-teal', l: 'Invited' }, in_progress: { c: 'gtt-p-teal', l: 'In progress' }, completed: { c: 'gtt-p-slate', l: 'Scoring' }, abandoned: { c: 'gtt-p-slate', l: 'Scoring' }, expired: { c: 'gtt-p-slate', l: 'Invite expired' } };
      const sp = row.interview_scoring === 'failed' ? { c: 'gtt-p-amber', l: 'Score failed' } : (STATE[row.interview_status] || { c: 'gtt-p-slate', l: String(row.interview_status || '').replace(/_/g, ' ') });
      return <span className={`gtt-pill ${sp.c}`} title={'Interview' + forRole}>{sp.l}</span>;
    }
    case 'temp': // C1-EMAIL
      return <TempDot row={row} label />;
    case 'yesno':
      return v ? <span className="gtt-pill gtt-p-green">Yes</span> : <span className="gtt-pill gtt-p-slate">No</span>;
    case 'text':
    default:
      return v ? <span className="gtt-ctext">{v}</span> : <span className="gtt-dash">—</span>;
  }
}

export default function GtTable({ columns, rows, loading, selectedIds, onToggle, onToggleAll, onCandidateClick, allTags, onTagToggle, sort, onSort, emptyText, onRowClick, rowClass, noSelect }) {
  const allSelected = rows.length > 0 && selectedIds && rows.every(r => selectedIds.has(r.id));
  let leftAcc = noSelect ? 0 : 38;
  const leftOffsets = {};
  columns.forEach(col => { if (col.frozen) { leftOffsets[col.key] = leftAcc; leftAcc += col.width; } });

  return (
    <div className="gtt-scroll">
      <table className="gtt-table">
        <thead>
          <tr>
            {!noSelect && <th className="gtt-checkcol gtt-frozen" style={{ left: 0 }}>
              <input type="checkbox" checked={!!allSelected} onChange={onToggleAll} aria-label="Select all on this page" />
            </th>}
            {columns.map(col => {
              const sortable = !!col.sort && typeof onSort === 'function';
              const active = sortable && sort && sort.field === col.sort;
              return (
                <th key={col.key} style={{ width: col.width, minWidth: col.width, ...(col.frozen ? { left: leftOffsets[col.key], position: 'sticky', zIndex: 3 } : {}) }} className={col.frozen ? 'gtt-frozen' : undefined}>
                  {sortable ? (
                    <button type="button" className={'gtt-sortbtn' + (active ? ' gtt-sortbtn--on' : '')} onClick={() => onSort(col.sort)}>
                      {col.label}
                      <span className="gtt-sortarrow">{active ? (sort.dir === 'asc' ? '↑' : '↓') : '↕'}</span>
                    </button>
                  ) : col.label}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={columns.length + (noSelect ? 0 : 1)} className="gtt-empty"><GtLoader compact /></td></tr>
          ) : rows.length === 0 ? (
            <tr><td colSpan={columns.length + (noSelect ? 0 : 1)} className="gtt-empty">{emptyText || 'No candidates match. Adjust filters or upload a CV.'}</td></tr>
          ) : rows.map(row => {
            const sel = selectedIds && selectedIds.has(row.id);
            return (
              <tr key={row.id} className={[sel ? 'gtt-rowsel' : '', onRowClick ? 'gtt-rowclick' : '', rowClass ? rowClass(row) : ''].filter(Boolean).join(' ') || undefined}
                onClick={onRowClick ? (e) => { if (!e.target.closest('a, button, input, label')) onRowClick(row); } : undefined}>
                {!noSelect && <td className="gtt-checkcol gtt-frozen" style={{ left: 0 }} onClick={e => e.stopPropagation()}>
                  <input type="checkbox" checked={!!sel} onChange={() => onToggle && onToggle(row.id)} aria-label="Select" />
                </td>}
                {columns.map(col => (
                  <td key={col.key} style={{ width: col.width, minWidth: col.width, ...(col.frozen ? { left: leftOffsets[col.key], position: 'sticky', zIndex: 1 } : {}) }} className={col.frozen ? 'gtt-frozen' : undefined}>
                    <Cell col={col} row={row} allTags={allTags} onTagToggle={onTagToggle} onCandidateClick={onCandidateClick} />
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
